import { formatCopenhagenDateTime } from '@/lib/clinicDateTime';

export { formatCopenhagenDate, formatCopenhagenDateTime, formatCopenhagenTime } from '@/lib/clinicDateTime';

export const PATIENT_EMAIL_TYPES = [
  'invoice',
  'invoice_reminder',
  'quote',
  'appointment_confirmation',
  'appointment_reminder',
  'appointment_cancelled',
  'appointment_rescheduled',
  'booking_confirmation',
  'questionnaire_invitation',
  'questionnaire_reminder',
  'pre_visit_questionnaire',
  'portal_invitation',
  'portal_access_granted',
  'patient_message',
  'consent_request',
  'manual',
] as const;

export type PatientEmailType = (typeof PATIENT_EMAIL_TYPES)[number];

export const PATIENT_EMAIL_HISTORY_STATUSES = [
  'sent',
  'delivered',
  'bounced',
  'complained',
  'failed',
] as const;

export type PatientEmailStatus = (typeof PATIENT_EMAIL_HISTORY_STATUSES)[number];

export function normalizePatientEmailHistoryStatus(status: string): PatientEmailStatus {
  switch (status) {
    case 'sent':
    case 'delivered':
    case 'bounced':
    case 'complained':
    case 'failed':
      return status;
    default:
      return 'sent';
  }
}

const TYPE_LABELS: Record<PatientEmailType, { key: string; fallback: string }> = {
  invoice: { key: 'email.type_invoice', fallback: 'Invoice' },
  invoice_reminder: { key: 'email.type_invoice_reminder', fallback: 'Payment reminder' },
  quote: { key: 'email.type_quote', fallback: 'Quote' },
  appointment_confirmation: {
    key: 'email.type_appointment_confirmation',
    fallback: 'Appointment confirmation',
  },
  appointment_reminder: {
    key: 'email.type_appointment_reminder',
    fallback: 'Appointment reminder',
  },
  appointment_cancelled: {
    key: 'email.type_appointment_cancelled',
    fallback: 'Cancellation',
  },
  appointment_rescheduled: {
    key: 'email.type_appointment_rescheduled',
    fallback: 'Rescheduled',
  },
  booking_confirmation: {
    key: 'email.type_booking_confirmation',
    fallback: 'Booking confirmation',
  },
  questionnaire_invitation: {
    key: 'email.type_questionnaire_invitation',
    fallback: 'Questionnaire',
  },
  questionnaire_reminder: {
    key: 'email.type_questionnaire_reminder',
    fallback: 'Questionnaire reminder',
  },
  pre_visit_questionnaire: {
    key: 'email.type_pre_visit_questionnaire',
    fallback: 'Pre-visit questionnaire',
  },
  portal_invitation: {
    key: 'email.type_portal_invitation',
    fallback: 'Portal invitation',
  },
  portal_access_granted: {
    key: 'email.type_portal_access_granted',
    fallback: 'Portal access',
  },
  patient_message: {
    key: 'email.type_patient_message',
    fallback: 'New message',
  },
  consent_request: {
    key: 'email.type_consent_request',
    fallback: 'Consent request',
  },
  manual: { key: 'email.type_manual', fallback: 'Manual email' },
};

function isPatientEmailType(value: string): value is PatientEmailType {
  return (PATIENT_EMAIL_TYPES as readonly string[]).includes(value);
}

/** dd-mm-åååå kl. HH:MM in Europe/Copenhagen. */
export function formatCopenhagenEmailStamp(iso: string): string {
  return formatCopenhagenDateTime(iso);
}

export function patientEmailTypeLabel(
  emailType: string,
  t: (key: string, fallback: string) => string,
): string {
  if (isPatientEmailType(emailType)) {
    const mapped = TYPE_LABELS[emailType];
    switch (emailType) {
      case 'invoice':
      case 'invoice_reminder':
      case 'quote':
      case 'appointment_confirmation':
      case 'appointment_reminder':
      case 'appointment_cancelled':
      case 'appointment_rescheduled':
      case 'booking_confirmation':
      case 'questionnaire_invitation':
      case 'questionnaire_reminder':
      case 'pre_visit_questionnaire':
      case 'portal_invitation':
      case 'portal_access_granted':
      case 'patient_message':
      case 'consent_request':
      case 'manual':
        return t(mapped.key, mapped.fallback);
      default: {
        const _exhaustive: never = emailType;
        return _exhaustive;
      }
    }
  }
  return emailType;
}
